// screens/ChangePasswordScreen.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useLang } from '../contexts/LanguageContext';
import { getErrorMessage } from '../utils/api';
import LanguageToggle from '../components/LanguageToggle';

export default function ChangePasswordScreen({ forced = false }) {
  const { changePassword, user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useLang();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);

  const requirements = [
    { check: next.length >= 12, label: t('pw.req.length') },
    { check: /[A-Z]/.test(next), label: t('pw.req.upper') },
    { check: /[a-z]/.test(next), label: t('pw.req.lower') },
    { check: /[0-9]/.test(next), label: t('pw.req.number') },
    { check: /[^A-Za-z0-9]/.test(next), label: t('pw.req.special') },
  ];
  const allMet = requirements.every(r => r.check);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!allMet) { setError(t('pw.notmet')); return; }
    if (next !== confirm) { setError(t('pw.nomatch')); return; }
    if (!forced && !current) { setError(t('pw.current.required')); return; }

    setLoading(true);
    try {
      await changePassword(forced ? null : current, next);
      // Forced first-login change flips must_change_password to false; without an
      // explicit redirect the screen would re-render in non-forced mode and ask for
      // the "current" password again (the reported second step). Go straight home.
      toast.success(t('pw.updated'));
      navigate('/', { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', minHeight: '100dvh',
      background: 'var(--bg-primary)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: 24, position: 'relative'
    }}>
      <div style={{ position: 'absolute', top: 16, right: 16 }}><LanguageToggle /></div>
      <div style={{ width: '100%', maxWidth: 440, animation: 'slideUp 0.3s ease' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🔐</div>
          <h1 style={{ fontSize: '1.5rem', marginBottom: 8 }}>
            {forced ? t('pw.set.title') : t('pw.change.title')}
          </h1>
          {forced && (
            <div className="warning-banner" style={{ marginTop: 12, textAlign: 'left' }}>
              <span>⚠</span>
              <span>{t('pw.forced.note')}</span>
            </div>
          )}
        </div>

        <div className="card" style={{ padding: 28 }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {!forced && (
              <div className="form-group">
                <label className="form-label">{t('pw.current')}</label>
                <input type={showPw ? 'text' : 'password'} value={current}
                  onChange={e => setCurrent(e.target.value)}
                  className="input-field" placeholder={t('pw.current.ph')} />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">{t('pw.new')}</label>
              <input type={showPw ? 'text' : 'password'} value={next}
                onChange={e => setNext(e.target.value)}
                className="input-field" placeholder={t('pw.new.ph')} />
            </div>

            <div className="form-group">
              <label className="form-label">{t('pw.confirm')}</label>
              <input type={showPw ? 'text' : 'password'} value={confirm}
                onChange={e => setConfirm(e.target.value)}
                className="input-field" placeholder={t('pw.confirm.ph')} />
            </div>

            {/* Requirements */}
            <div style={{ background: 'var(--bg-input)', borderRadius: 'var(--radius-md)', padding: 14 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 8 }}>{t('pw.requirements')}</div>
              {requirements.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: '0.875rem' }}>
                  <span style={{ color: r.check ? 'var(--accent-success)' : 'var(--text-muted)', fontWeight: 700 }}>{r.check ? '✓' : '○'}</span>
                  <span style={{ color: r.check ? 'var(--text-secondary)' : 'var(--text-muted)' }}>{r.label}</span>
                </div>
              ))}
            </div>

            <label className="toggle-wrap">
              <div className={`toggle ${showPw ? 'on' : ''}`} />
              <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{t('pw.show')}</span>
            </label>

            {error && <div className="error-banner"><span>⚠</span><span>{error}</span></div>}

            <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading || !allMet || !next || !confirm}>
              {loading ? t('pw.updating') : t('pw.submit')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
